import { useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

let socket = null;

// ✅ 1. Add `onToggleVideo` to the destructured props
export const useSignal = ({ user, isBroadcaster, onOffer, onAnswer, onIce, onActiveUsers, onToggleVideo }) => {
    const socketRef = useRef(null);

    // ✅ 2. Add `onToggleVideo` to the callbacks ref (prevents stale closures)
    const callbacks = useRef({ onOffer, onAnswer, onIce, onActiveUsers, onToggleVideo });

    useEffect(() => {
        callbacks.current = { onOffer, onAnswer, onIce, onActiveUsers, onToggleVideo };
    }, [onOffer, onAnswer, onIce, onActiveUsers, onToggleVideo]);

    useEffect(() => {
        if (!user || !user._id) return;

        if (!socket) {
            console.log("🔌 [Signal] Initializing Global Socket...");
            socket = io(API_URL, {
                transports: ['websocket'],
                reconnection: true,
                reconnectionAttempts: 10
            });
        }
        socketRef.current = socket;

        const handleConnect = () => {
            console.log(`✅ [Signal] Connected: ${socket.id}`);
            socket.emit("join-room", { userId: user._id });
            socket.emit("get-active-users");
        };

        const handleOffer = (data) => {
            if (isBroadcaster && callbacks.current.onOffer) callbacks.current.onOffer(data); 
        };

        const handleAnswer = (data) => {
            if (!isBroadcaster && callbacks.current.onAnswer) callbacks.current.onAnswer(data); 
        };

        const handleIce = (data) => {
            if (callbacks.current.onIce) callbacks.current.onIce(data.candidate); 
        };

        const handleActiveUsers = (data) => {
            if (callbacks.current.onActiveUsers) callbacks.current.onActiveUsers(data); 
        };

        // ✅ 3. Define the Handler for Video Toggle
        const handleToggleVideo = (data) => {
            // data typically looks like: { enabled: true/false }
            console.log("📡 [Signal] Received Toggle Video Event:", data);
            if (callbacks.current.onToggleVideo) callbacks.current.onToggleVideo(data);
        };

        // Attach Listeners 
        socket.on("connect", handleConnect);
        socket.on("webrtc-offer", handleOffer);
        socket.on("webrtc-answer", handleAnswer);
        socket.on("webrtc-ice", handleIce);
        socket.on("active-users", handleActiveUsers);
        
        // ✅ 4. Attach the Socket Listener
        socket.on("toggle-video", handleToggleVideo);

        if (socket.connected) {
            socket.emit("join-room", { userId: user._id });
            socket.emit("get-active-users");
        }

        return () => {
            socket.off("connect", handleConnect);
            socket.off("webrtc-offer", handleOffer);
            socket.off("webrtc-answer", handleAnswer);
            socket.off("webrtc-ice", handleIce);
            socket.off("active-users", handleActiveUsers);
            
            // ✅ 5. Cleanup the Listener
            socket.off("toggle-video", handleToggleVideo);
        };

    }, [user?._id, isBroadcaster]); 

    const sendOffer = useCallback(({ to, offer }) => {
        if (socketRef.current) socketRef.current.emit("webrtc-offer", { to, offer });
    }, []);

    const sendAnswer = useCallback(({ to, answer }) => {
        if (socketRef.current) socketRef.current.emit("webrtc-answer", { to, answer });
    }, []);

    const sendIce = useCallback(({ to, candidate }) => {
        if (socketRef.current) socketRef.current.emit("webrtc-ice", { to, candidate });
    }, []);

    const sendToggleVideo = useCallback(({ to, enabled }) => {
        if (socketRef.current) socketRef.current.emit("toggle-video", { to, enabled });
    }, []);

    const sendToggleAudio = useCallback(({ to, enabled }) => {
        if (socketRef.current) socketRef.current.emit("toggle-audio", { to, enabled });
    }, []);

    return {
        socketRef,
        sendOffer,
        sendAnswer,
        sendIce,
        sendToggleVideo,
        sendToggleAudio
    };
};